/**
 * "Waiting on a decision" — `studio/clients.html`'s ochre card, and the states fold
 * that closes the page.
 *
 * A decision is **every decision open on any account** (`readmodels/formulae.md`
 * §Waiting on a decision): a proposed quote, extra, package change or content plan
 * sitting with the client, an open decision-tagged task chasing one, and a draft the
 * studio still owes a price for. Each row says which side it is waiting on, and the
 * studio's own sort first, because those are the ones anybody here can act on today.
 *
 * **The empty state names the set.** It used to read "Nothing is waiting on the
 * studio. No account has an open decision." over a payload built from
 * decision-tagged tasks alone — and the seeded world has exactly one such task, which
 * is cancelled. So the page announced that nothing needed chasing while five
 * proposals were open and a quote was two days overdue. DESIGN.md P7 gives an empty
 * state one line; this one spends it saying what was looked for.
 *
 * **The button goes to the client, the reference goes to the thing.** The mockup's
 * four buttons all say "Open" and three of them point at `client-detail.html`; the
 * fourth points at `money.html`, which is a decision this page cannot make from a row
 * alone. So "Open" is always the account — the screen that has every figure about it
 * — and only a task has a board of its own to link to.
 */

import type { StudioClientRowView, StudioClientsListView } from '@fareast/shared'
import { Link } from '@tanstack/react-router'

import { Button, ButtonRow } from '../../../components/portal/Button.js'
import { Card, CardBody } from '../../../components/portal/Card.js'
import { Empty, Fold } from '../../../components/portal/Disclosure.js'
import { Kv, KvRow } from '../../../components/portal/Kv.js'
import { Section, SectionHead } from '../../../components/portal/Layout.js'
import { NeedsBar, NeedsItem } from '../../../components/portal/Status.js'
import { B, Linkish, More, Muted, Num, Prose, Ref, Small } from '../../../components/portal/Text.js'
import { STATUS_ORDER, bookCounts, statusWord } from './ClientsBook.js'

/** The anchor the hero's "The four →" link points at. */
export const DECISIONS_ANCHOR = 'decisions'

export type DecisionsSectionProps = {
  readonly decisions: StudioClientsListView['decisions']
  /** The rows on screen — what the states fold counts. */
  readonly rows: readonly StudioClientRowView[]
  /** Which seg is on, so the fold knows which states can possibly appear. */
  readonly filter: StudioClientsListView['filter']
  /** What the seg lists, as a noun — "active clients" — so it can say what it is counting. */
  readonly filterClients: string
}

export function DecisionsSection({
  decisions,
  rows,
  filter,
  filterClients,
}: DecisionsSectionProps) {
  const { byStatus } = bookCounts(rows)
  /*
   * **Only the states this seg can produce.**
   *
   * `studio-clients-list.ts` `matchesFilter` is one-to-one — `active` admits exactly
   * `active`, `past_due` exactly `past_due` — so on a narrowed tab four of the five
   * rows below are zero *by construction*, and one of them is headed by the same word
   * as the tab sitting beside it. Session 24 (R2-35) found "Past due 0" under a tab
   * labelled "Past due" that holds two clients: both true, and together nonsense.
   *
   * A caption saying "counted over the clients listed above" was not enough, because
   * the reader has to read a paragraph to discover that four rows of a five-row table
   * could never have said anything. So the rows the filter forbids are not printed at
   * all, and the fold says where the rest of the census is instead.
   */
  const states = filter === 'all' ? STATUS_ORDER : STATUS_ORDER.filter((one) => one === filter)

  return (
    <Section id={DECISIONS_ANCHOR}>
      <SectionHead title="Waiting on a decision" />

      <Card need>
        <CardBody tight>
          {decisions.length === 0 ? (
            <Empty>
              No proposal, chase or overdue quote is open on any account — nothing is sitting with a
              client and nothing is ours to send.
            </Empty>
          ) : (
            decisions.map((decision) => (
              <NeedsItem key={`${decision.kind}-${decision.ref}`}>
                <NeedsBar />
                <div>
                  <B>{decision.title}</B>
                  <Small>
                    <Muted>
                      {decision.task_ref === null ? (
                        <Ref value={decision.ref} />
                      ) : (
                        <Linkish asChild>
                          <Link params={{ ref: decision.task_ref }} to="/studio/tasks/$ref">
                            <Ref value={decision.task_ref} />
                          </Link>
                        </Linkish>
                      )}{' '}
                      &middot; {decision.client} &middot; {waitingWord(decision)}
                    </Muted>
                  </Small>
                </div>
                <ButtonRow>
                  <Button asChild size="sm">
                    <Link params={{ slug: decision.client }} to="/studio/clients/$slug">
                      Open
                    </Link>
                  </Button>
                </ButtonRow>
              </NeedsItem>
            ))
          )}
        </CardBody>
      </Card>

      <Fold summary="Retainer states, and what pausing or cancelling does">
        <Kv bare>
          {states.map((status) => (
            <KvRow
              k={statusWord(status)}
              key={status}
              v={<Num value={byStatus.get(status) ?? 0} />}
            />
          ))}
        </Kv>
        {filter === 'all' ? (
          <Prose>
            Every client in the book, counted over the list above. Work pauses at 14 days past due;
            widgets keep running until their cap either way.{' '}
            <More asChild>
              <Link to="/studio/money">Past due, and what is being retried &rarr;</Link>
            </More>
          </Prose>
        ) : (
          <Prose>
            The seg above is showing <B>{filterClients}</B> only, so this is the one state the list
            can hold — a client in any other is not counted here at all. Work pauses at 14 days past
            due; widgets keep running until their cap either way.{' '}
            <More asChild>
              <Link search={{ status: 'all' }} to="/studio/clients">
                Every state, over the whole book &rarr;
              </Link>
            </More>
          </Prose>
        )}
        <Prose>
          Cancellation takes effect at period end and the current period is never refunded — a
          retainer that ends on the 31st was billed for that month in full.
        </Prose>
      </Fold>
    </Section>
  )
}

/** How many distinct clients are waiting. What the sub-figure counts. */
export function clientsWaiting(decisions: StudioClientsListView['decisions']): number {
  return new Set(decisions.map((one) => one.client)).size
}

/** How many of them the studio is holding up — the only ones anybody here can move. */
export function ownedByStudio(decisions: StudioClientsListView['decisions']): number {
  return decisions.filter((one) => one.waiting_on === 'studio').length
}

/**
 * Which side a row is waiting on, in words.
 *
 * `waiting_on` is a field rather than a rendering because the read model is the one
 * place that knows a draft past its `quote_due_on` is the studio's and a proposal is
 * the client's — see `readmodels/formulae.md` §Waiting on a decision.
 */
function waitingWord(decision: StudioClientsListView['decisions'][number]): string {
  return decision.waiting_on === 'studio' ? 'ours to send' : 'with the client'
}
